import { Button } from 'components/common/Button'
import { CheckboxGroup } from 'components/common/CheckboxGroup'
import { ConfirmModal } from 'components/common/ConfirmModal'
import { Icon } from 'components/common/Icon'
import { Info } from 'components/common/Info'
import { useOverlay } from 'contexts'
import { Form, Formik, FormikProps, FormikValues } from 'formik'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { Edition } from 'utils/data'
import { getArtworkUrlFromRoot } from 'utils/urls'

type Props = {
  currentEdition?: Edition
  artwork?: {
    artists: { id: number; name: string }[]
    name: string
    id: number
  }
}

export const SkipAuthModal = ({
  currentEdition,
  artwork: { artists, name, id } = { artists: [], name: '', id: 0 },
}: Props) => {
  const { t } = useTranslation()
  const { setIsOpen } = useOverlay()
  const router = useRouter()
  const { query } = router
  const { tradeType, tradeValue } = query

  const [necessaryDocs, setNecessaryDocs] = useState<
    { name: string; value: string; tooltip?: string; noColorStyle?: boolean }[]
  >([])

  const formikRef = useRef<FormikProps<FormikValues>>(null)

  useEffect(() => {
    if (!currentEdition?.additionalCertification) return

    const { additionalCertification } = currentEdition

    const newDocs: typeof necessaryDocs = []

    if (additionalCertification.artworkImages)
      newDocs.push({
        name: t('authentication:images_of_the_artwork'),
        value: 'images',
      })

    if (additionalCertification.coaImages)
      newDocs.push({
        name: t('authentication:certificate_of_authenticity'),
        value: 'certificate',
        tooltip: t('authentication:the_original_coa'),
        noColorStyle: true,
      })

    if (additionalCertification.editionNumber)
      newDocs.push({
        name: t('authentication:edition_number'),
        value: 'editionNumber',
      })

    if (additionalCertification.provenanceImages)
      newDocs.push({
        name: t('authentication:provenance_images'),
        value: 'provenanceImages',
        tooltip: t('authentication:one_or_more_of_coa'),
        noColorStyle: true,
      })

    setNecessaryDocs(newDocs)
  }, [currentEdition])

  return (
    <ConfirmModal
      title={
        <div className='flex space-x-2'>
          <Icon
            filled
            name='filled-alert-info'
            className='text-warning-default'
            size={22}
          />
          <h5 className='font-body text-base font-medium tracking-normal'>
            {t('authentication:skip_authentication')}
          </h5>
        </div>
      }
      showCloseButton={false}
      body={
        <div>
          <p className='text-sm text-cool-800'>
            {t('authentication:upon_the_sale')}
          </p>

          <Formik
            innerRef={formikRef}
            initialValues={{ providedDocs: [] }}
            onSubmit={async ({ providedDocs }, { setErrors }) => {
              const errors: Record<string, string> = {}

              necessaryDocs.forEach(doc => {
                if (!providedDocs.includes(doc.value)) {
                  errors[doc.value] = t('authentication:field_required')
                }
              })

              if (Object.keys(errors).length) {
                setErrors(errors)
                return
              }

              setIsOpen(false)
            }}
          >
            {({ errors }) => {
              return (
                <Form className='mt-6'>
                  <CheckboxGroup
                    name='providedDocs'
                    fields={necessaryDocs}
                    errors={errors as Record<string, string>}
                  />
                </Form>
              )
            }}
          </Formik>

          <Info className='mt-6'>{t('authentication:if_you_fail')}</Info>
        </div>
      }
      confirmButton={
        <Button
          type='submit'
          onClick={() => {
            formikRef.current?.submitForm()
          }}
        >
          {t('common:confirm')}
        </Button>
      }
      onCancel={() => {
        router.push({
          query: {
            askId: query.askId,
            tradeType,
            tradeValue,
          },

          pathname:
            getArtworkUrlFromRoot('ask-or-sell', artists, name, id) +
            `/${currentEdition?.name}`,
        })
      }}
    />
  )
}
